import {
    HttpClient,
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { CreateHotToastRef } from '@ngxpert/public-api';
import { convertRequest, convertResponse, deepCopy } from 'app/main/shared/utils';
import { getTimezoneOffset } from 'date-fns-tz';
import { environment } from 'environments/environment';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AuthServerProvider } from '../auth/auth-jwt.service';
import { PrincipalService } from '../auth/principal.service';
import { DialogService } from '../components/dialog/service/dialog.service';
import { EventManager } from '../service/event-manager.service';

@Injectable()
export class GlobalHttpInterceptor implements HttpInterceptor {
    retryCount = 3;
    retryWaitMilliSeconds = 1000;
    lang = 'en';

    // Token refresh state management
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    private em = inject(EventManager);
    private _principal = inject(PrincipalService);
    private _translateService = inject(TranslateService);
    private _progressService = inject(FuseProgressBarService);
    private _dialogService = inject(DialogService);
    private httpClient = inject(HttpClient);
    private authService = inject(AuthServerProvider);
    private gatewayErrorRef: CreateHotToastRef<any>;
    private userTimeZoneOffset: string;

    constructor() {
        this.userTimeZoneOffset = new Date().getTimezoneOffset() + '';
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!navigator.onLine) {
            return EMPTY;
        }

        // Add authentication and headers
        request = this.addAuthHeaders(request);
        request = this.addCommonHeaders(request);

        if (environment.development) {
            console.log('REQUEST', request);
        }

        const started = Date.now();

        if (!request.headers.has('hide-progress')) {
            this._progressService.show();
        }

        return next.handle(request).pipe(
            tap({
                next: (event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse) {
                        if (!request.headers.has('hide-progress')) {
                            this._progressService.hide();
                        }
                        let data = event.body;
                        if (!request.headers.has('original-data')) {
                            data = convertResponse(data);
                        }
                        if (environment.development) {
                            console.log('RESPONSE-DATA', data);
                        }
                        // If access token is changed, update the principal
                        if (event.headers.has('authorization')) {
                            const token = event.headers.get('authorization');
                            if (token?.length > 0 && token !== this._principal.accessToken) {
                                this._principal.accessToken = token;
                            }
                        }
                    }
                    return event;
                },
                complete: () => {
                    const elapsed = Date.now() - started;
                    console.log(`Http request completed: ${elapsed} ms `);
                    this.em.broadcast({
                        name: 'button-clicked',
                        content: {},
                    });
                },
            }),
            catchError((error: HttpErrorResponse) => {
                this.em.broadcast({
                    name: 'button-clicked',
                    content: { error: error },
                });
                if (!request.headers.has('hide-progress')) {
                    this._progressService.hide();
                }
                return this.errorHandle(error, request, next);
            })
        );
    }

    private addAuthHeaders(request: HttpRequest<any>): HttpRequest<any> {
        if (this._principal.accessToken?.length > 0) {
            request = request.clone({
                headers: request.headers.set('Authorization', `Bearer ${this._principal.accessToken}`),
            });
        }
        return request;
    }

    private addCommonHeaders(request: HttpRequest<any>): HttpRequest<any> {
        if (this._principal.isAuthenticated) {
            const user = this._principal.authenticatedUser;
            if (environment.timezoneOffset == -1 && user?.activeSchool?.timeZone) {
                environment.timezoneOffset = getTimezoneOffset(user.activeSchool.timeZone, new Date()) / (1000 * 60);
            }
            request = request.clone({
                headers: request.headers
                    .set('X-TENANT', user.tenant ? user.tenantId : '')
                    .set('X-LANG', this._translateService.currentLang ?? 'en')
                    .set('X-DATALANGUAGE', user.activeSchool?.dataLanguage ?? 'en')
                    .set('X-SCHOOL', user.activeSchool ? user.activeSchool.id : '')
                    .set('X-ACADEMIC_PERIOD', user.academicYear ? user.academicYear.id : '')
                    .set('X-Platform', 'web')
                    .set('X-DEPARTMENT', this._principal.activeDepartmentId() ?? '')
                    .set('X-NAME_ORDER', user.activeSchool?.nameOrder ?? 'FIRST_LAST')
                    .set('X-VERSION', environment.version)
                    .set('X-ZONE', Intl.DateTimeFormat().resolvedOptions().timeZone)
                    .set('X-Timezone-Offset', this.userTimeZoneOffset),
            });
        } else {
            request = request.clone({
                headers: request.headers
                    .set('X-ZONE', Intl.DateTimeFormat().resolvedOptions().timeZone)
                    .set('Content-Type', 'application/json')
                    .set('X-VERSION', environment.version)
                    .set('X-Platform', 'web')
                    .set('X-Timezone-Offset', this.userTimeZoneOffset),
            });
        }

        if (request.body && request.body instanceof FormData) {
            request = request.clone({
                headers: request.headers.delete('content-type'),
                reportProgress: true,
            });
        } else {
            request = request.clone({
                headers: request.headers.set('Content-Type', 'application/json'),
            });
        }

        if (request.body && !(request.body instanceof FormData)) {
            let data = deepCopy(request.body);
            data = convertRequest(data);
            request = request.clone({
                body: data,
            });
        }

        return request;
    }

    errorHandle(error: any, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (environment.development) {
            console.log('REQUEST-ERROR', error);
        }
        this.em.broadcast({
            name: 'button-clicked',
            content: { error: error },
        });

        if (error.status == 504) {
            this.httpClient.get<any>('api-public/ping').subscribe(
                () => {
                    console.log('Running ... [OK]');
                    if (this.gatewayErrorRef) {
                        this.gatewayErrorRef.close();
                        this.gatewayErrorRef = undefined;
                    }
                    this._dialogService.close('gateway_error_message');
                },
                () => {
                    this.gatewayErrorRef = this._dialogService.info(
                        'We`re currently experiencing some technical issues. Please try again later!',
                        {
                            id: 'gateway_error_message',
                            dismissible: false,
                            autoClose: false,
                        }
                    );
                }
            );
            return throwError(() => error);
        }
        // unauthorized
        else if (error.status == 401) {
            console.log('401 ERROR', error);
            if (request.url.indexOf('/auth/login') == -1) {
                if (
                    request.url.indexOf('/auth/refresh-token') == -1 &&
                    this._principal.isAuthenticated &&
                    this._principal.accessToken?.length > 0
                ) {
                    return this.refreshTokenAndRetry(request, next);
                } else {
                    /*
                    setTimeout(() => {
                        this._dialogService.error(error.message, null, {
                            id: 'auth',
                            position: 'bottom-right',
                            duration: 3000,
                        });
                    }, 1000);
                    */
                    this._principal.closeSession();
                    this.em.broadcast({
                        name: 'show-login',
                        content: {},
                    });
                }
            } else {
                this._dialogService.error(error.error.detail, null, {
                    id: 'auth',
                    position: 'bottom-right',
                    duration: 1000,
                });
            }
        } else if (error.status == 400 && !this._principal.isAuthenticated) {
            this._dialogService.error(error.error.message, error.error.detail, {
                id: 'auth',
                position: 'bottom-right',
                duration: 3000,
            });
        } else {
            return throwError(() => error);
        }

        return throwError(() => error);
    }

    private refreshTokenAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return new Observable(observer => {
                this.authService
                    .refreshToken()
                    .then(() => {
                        this.isRefreshing = false;
                        const newToken = this._principal.accessToken;
                        this.refreshTokenSubject.next(newToken);

                        // Retry the original request with new token
                        const retryRequest = request.clone({
                            headers: request.headers.set('Authorization', `Bearer ${newToken}`),
                        });

                        next.handle(retryRequest).subscribe({
                            next: event => observer.next(event),
                            error: err => observer.error(err),
                            complete: () => observer.complete(),
                        });
                    })
                    .catch(refreshError => {
                        this.isRefreshing = false;
                        this.refreshTokenSubject.next(null);

                        console.error('Token refresh failed:', refreshError);
                        this._principal.closeSession();
                        this.em.broadcast({
                            name: 'show-login',
                            content: {},
                        });
                        return Promise.reject(refreshError);
                    });
            });
        } else {
            // Token refresh is already in progress, wait for it
            return this.refreshTokenSubject.pipe(
                filter((token: string) => token != null),
                take(1),
                switchMap((token: string) => {
                    if (token?.length > 0) {
                        const retryRequest = request.clone({
                            headers: request.headers.set('Authorization', `Bearer ${token}`),
                        });
                        return next.handle(retryRequest);
                    } else {
                        this.em.broadcast({
                            name: 'show-login',
                            content: {},
                        });
                    }
                })
            );
        }
    }

    getTimezoneOffset(): string {
        return String(new Date().getTimezoneOffset());
    }
}
